import { createSelector } from 'reselect'

import { channelToParticipantToken, pickOtherParticipant } from './converters'
import type { ChatServerState } from './state'
import type { ChatServerChannel, ChatServerMessage, ChatServerParticipant } from './types'

export type ChatServerParticipantWithPresence = ChatServerParticipant & { online: boolean }

export type ChatServerSelectors<S> = ReturnType<typeof createChatServerSelectors<S>>

/**
 * Build the selector suite over a host state `S`, given a lens to the mounted
 * `ChatServerState`. The web front feeds its redux-slice selector; a classic
 * store feeds `state => state.chatServer`.
 */
export const createChatServerSelectors = <S>(chatStateSelector: (state: S) => ChatServerState) => {
  const chatServerChannelsSelector = (state: S) => chatStateSelector(state).channels
  const chatServerParticipantsSelector = (state: S) => chatStateSelector(state).participants
  const chatServerMessagesSelector = (state: S) => chatStateSelector(state).messages
  const chatServerResolvedSelector = (state: S) => chatStateSelector(state).resolved
  const chatServerGlobalPresenceSelector = (state: S) => chatStateSelector(state).globalPresence
  const chatServerTypingSelector = (state: S) => chatStateSelector(state).typing
  const chatServerUnreadSelector = (state: S) => chatStateSelector(state).unread

  /** Resolved display identity (name/avatar/kind) for a participant external_id. */
  const makeResolvedParticipantSelector = (externalId: string | null) => (state: S) =>
    externalId ? (chatServerResolvedSelector(state)[externalId] ?? null) : null

  const chatServerConnectedSelector = (state: S) => chatStateSelector(state).connected
  const chatServerReadySelector = (state: S) => chatStateSelector(state).ready
  const chatServerConnectFailedSelector = (state: S) => chatStateSelector(state).connectFailed
  const chatServerChannelsStatusSelector = (state: S) => chatStateSelector(state).channelsStatus

  const chatServerTotalUnreadSelector = createSelector([chatServerUnreadSelector], unread => {
    let total = 0
    for (const value of Object.values(unread)) total += value
    return total
  })

  const makeChannelByIdSelector = (channelId: string | null) => (state: S) => (channelId ? (chatServerChannelsSelector(state)[channelId] ?? null) : null)

  /**
   * The current user's support channel (`type === 'support'`), routed as
   * `/m/support`. Scoped by participant: a stale channel left over from a
   * previous session would otherwise be joined and fetched as the new user,
   * which the server rejects (FRONTEND-90E).
   */
  const makeSupportChannelSelector = (currentUserId: string | number | null) =>
    createSelector(
      [chatServerChannelsSelector, chatServerParticipantsSelector],
      (channels, participants): ChatServerChannel | null =>
        Object.values(channels).find(channel => channel.type === 'support' && isSelfParticipant(channel, participants, currentUserId)) ?? null
    )

  /** Routes `/m/:token` → channel by scanning participants for `external_id === token`. */
  const makeChannelByUserTokenSelector = (token: string | null, currentUserId: string | number | null) =>
    createSelector([chatServerChannelsSelector, chatServerParticipantsSelector], (channels, participants): ChatServerChannel | null => {
      if (!token) return null
      for (const channel of Object.values(channels)) {
        for (const id of channel.participantIds) {
          const participant = participants[id]
          if (!participant) continue
          if (currentUserId !== null && participant.external_id !== null && String(participant.external_id) === String(currentUserId)) continue
          if (participant.external_id === token) return channel
        }
      }
      return null
    })

  /**
   * If `favIds` is null → all channels, ordered by `updated_at` desc (support
   * pinned first). Channels the current user archived (self-participant
   * `archived_at` set) are excluded — they live in the archived list.
   */
  const makeChannelIdsSelector = (favIds: string[] | null, currentUserId: string | number | null) =>
    createSelector([chatServerChannelsSelector, chatServerParticipantsSelector], (channels, participants) => {
      const favSet = favIds ? new Set(favIds) : null
      return Object.values(channels)
        .filter(channel => (favSet ? favSet.has(channel.id) : true))
        .filter(channel => !isSelfArchived(channel, participants, currentUserId))
        .sort(byPinnedSupportThenUpdatedAt)
        .map(channel => channel.id)
    })

  /** Channels the current user archived, ordered like the main list. Support is never archivable. */
  const makeArchivedChannelIdsSelector = (currentUserId: string | number | null) =>
    createSelector([chatServerChannelsSelector, chatServerParticipantsSelector], (channels, participants) =>
      Object.values(channels)
        .filter(channel => channel.type !== 'support' && isSelfArchived(channel, participants, currentUserId))
        .sort(byPinnedSupportThenUpdatedAt)
        .map(channel => channel.id)
    )

  /** Whether the current user archived this channel (the header menu label). */
  const makeChannelArchivedSelector = (channelId: string | null, currentUserId: string | number | null) =>
    createSelector([makeChannelByIdSelector(channelId), chatServerParticipantsSelector], (channel, participants) =>
      channel ? isSelfArchived(channel, participants, currentUserId) : false
    )

  const chatServerArchiveCountSelector = (state: S) => chatStateSelector(state).archiveCount

  /**
   * Union of two favorite marks: the peer's external_id ∈ `favoriteExternalIds` (kidsout
   * user favorites), or my own participant carries chat_server's native `favorited_at`.
   * One filter pass keeps it uniq; ordering as `makeChannelIdsSelector` (support pinned first).
   */
  const makeFavoriteChannelIdsSelector = (favoriteExternalIds: string[], currentUserId: string | number | null) =>
    createSelector([chatServerChannelsSelector, chatServerParticipantsSelector], (channels, participants) => {
      const favSet = new Set(favoriteExternalIds.map(String))
      return Object.values(channels)
        .filter(channel =>
          channel.participantIds.some(id => {
            const participant = participants[id]
            if (!participant?.external_id) return false
            const isSelf = currentUserId !== null && String(participant.external_id) === String(currentUserId)
            if (isSelf) return participant.favorited_at !== null
            return favSet.has(String(participant.external_id))
          })
        )
        .sort(byPinnedSupportThenUpdatedAt)
        .map(channel => channel.id)
    })

  const makeChannelUnreadSelector = (channelId: string | null) => (state: S) => (channelId ? (chatServerUnreadSelector(state)[channelId] ?? 0) : 0)

  const makeParticipantsByChannelSelector = (channelId: string | null) =>
    createSelector(
      [chatServerChannelsSelector, chatServerParticipantsSelector, chatServerGlobalPresenceSelector],
      (channels, participants, globalPresence): ChatServerParticipantWithPresence[] => {
        if (!channelId) return EMPTY_ARRAY
        const channel = channels[channelId]
        if (!channel) return EMPTY_ARRAY
        return channel.participantIds
          .map(id => participants[id])
          .filter((p): p is ChatServerParticipant => !!p)
          .map(p => ({ ...p, online: !!globalPresence[p.user_id] }))
      }
    )

  const makeOtherParticipantSelector = (channelId: string | null, currentUserId: string | number | null) =>
    createSelector([makeParticipantsByChannelSelector(channelId)], participants => pickOtherParticipant(participants, currentUserId))

  /**
   * Whether the other side is online. A support channel is answered by whichever operator is on
   * duty (a bot may sit in it too), so any other participant online counts; a direct one follows
   * its single peer.
   */
  const makeChannelOnlineSelector = (channelId: string | null, currentUserId: string | number | null) =>
    createSelector([makeChannelByIdSelector(channelId), makeParticipantsByChannelSelector(channelId)], (channel, participants) => {
      if (channel?.type !== 'support') return !!pickOtherParticipant(participants, currentUserId)?.online
      return participants.some(p => p.online && (currentUserId === null || String(p.external_id) !== String(currentUserId)))
    })

  const makeChannelParticipantTokenSelector = (channelId: string | null, currentUserId: string | number | null) =>
    createSelector([makeChannelByIdSelector(channelId), chatServerParticipantsSelector], (channel, participants) =>
      channel ? channelToParticipantToken(channel, participants, currentUserId) : null
    )

  /** Messages of a channel ordered by `inserted_at` asc. */
  const makeMessagesByChannelSelector = (channelId: string | null) =>
    createSelector([chatServerMessagesSelector], (messages): ChatServerMessage[] => {
      if (!channelId) return EMPTY_ARRAY
      const channel = messages[channelId]
      if (!channel) return EMPTY_ARRAY
      return Object.values(channel).sort((a, b) => insertedAtMs(a) - insertedAtMs(b))
    })

  const makeMessageByIdSelector =
    <A = Record<string, unknown>>(channelId: string | null, messageId: string | null) =>
    (state: S): ChatServerMessage<A> | null => {
      if (!channelId || !messageId) return null
      return (chatServerMessagesSelector(state)[channelId]?.[messageId] as ChatServerMessage<A> | undefined) ?? null
    }

  const makeLastMessageByChannelSelector = (channelId: string | null) =>
    createSelector([makeMessagesByChannelSelector(channelId)], messages => (messages.length ? messages[messages.length - 1] : null))

  /**
   * Channel-agnostic message lookup by id — used by entity leaf components
   * (request / announcement cards) that only carry a message sid.
   */
  const makeMessageBySidSelector =
    <A = Record<string, unknown>>(messageId: string | null) =>
    (state: S): ChatServerMessage<A> | null => {
      if (!messageId) return null
      const messages = chatServerMessagesSelector(state)
      for (const channel of Object.values(messages)) {
        const message = channel[messageId]
        if (message) return message as ChatServerMessage<A>
      }
      return null
    }

  /**
   * Whether the given participant has read up to a message timestamp. Returns a
   * predicate over message `inserted_at` for read-receipt rendering.
   */
  const makeReadReceiptSelector = (channelId: string | null, participantToken: string | null) =>
    createSelector([makeParticipantsByChannelSelector(channelId)], participants => {
      const participant = participantToken ? participants.find(p => p.external_id === participantToken) : null
      const lastReadAt = participant?.last_read_at ? Date.parse(participant.last_read_at) : 0
      return (messageInsertedAt: string | null) => {
        if (!messageInsertedAt) return false
        return Date.parse(messageInsertedAt) <= lastReadAt
      }
    })

  /** Returns the set of currently-typing user ids (TTL-filtered) for a channel. */
  const makeTypingSelector = (channelId: string | null) =>
    createSelector([chatServerTypingSelector], typing => {
      if (!channelId) return EMPTY_ARRAY
      const channel = typing[channelId]
      if (!channel) return EMPTY_ARRAY
      const now = Date.now()
      return Object.entries(channel)
        .filter(([, expiry]) => expiry > now)
        .map(([userId]) => userId)
    })

  return {
    chatServerChannelsSelector,
    chatServerParticipantsSelector,
    chatServerMessagesSelector,
    chatServerResolvedSelector,
    chatServerGlobalPresenceSelector,
    chatServerTypingSelector,
    chatServerUnreadSelector,
    chatServerConnectedSelector,
    chatServerReadySelector,
    chatServerConnectFailedSelector,
    chatServerChannelsStatusSelector,
    chatServerTotalUnreadSelector,
    makeSupportChannelSelector,
    chatServerArchiveCountSelector,
    makeResolvedParticipantSelector,
    makeChannelByIdSelector,
    makeChannelByUserTokenSelector,
    makeChannelIdsSelector,
    makeArchivedChannelIdsSelector,
    makeChannelArchivedSelector,
    makeFavoriteChannelIdsSelector,
    makeChannelUnreadSelector,
    makeParticipantsByChannelSelector,
    makeOtherParticipantSelector,
    makeChannelOnlineSelector,
    makeChannelParticipantTokenSelector,
    makeMessagesByChannelSelector,
    makeMessageByIdSelector,
    makeLastMessageByChannelSelector,
    makeMessageBySidSelector,
    makeReadReceiptSelector,
    makeTypingSelector,
  }
}

const EMPTY_ARRAY: never[] = []

/** Whether the current user is a participant of the channel. Unknown `currentUserId` cannot narrow anything, so it matches. */
const isSelfParticipant = (channel: ChatServerChannel, participants: Record<string, ChatServerParticipant>, currentUserId: string | number | null): boolean => {
  if (currentUserId === null) return true
  return channel.participantIds.some(id => {
    const participant = participants[id]
    return !!participant?.external_id && String(participant.external_id) === String(currentUserId)
  })
}

/** Whether the current user's own participant of the channel carries `archived_at` (archive is per-participant). */
const isSelfArchived = (channel: ChatServerChannel, participants: Record<string, ChatServerParticipant>, currentUserId: string | number | null): boolean => {
  if (currentUserId === null) return false
  for (const id of channel.participantIds) {
    const participant = participants[id]
    if (!participant?.external_id) continue
    if (String(participant.external_id) !== String(currentUserId)) continue
    return participant.archived_at !== null
  }
  return false
}

const byPinnedSupportThenUpdatedAt = (a: ChatServerChannel, b: ChatServerChannel) => {
  const supportDelta = (b.type === 'support' ? 1 : 0) - (a.type === 'support' ? 1 : 0)
  if (supportDelta !== 0) return supportDelta
  return updatedAtMs(b) - updatedAtMs(a)
}

const updatedAtMs = (channel: ChatServerChannel) => (channel.updated_at ? Date.parse(channel.updated_at) : 0)
const insertedAtMs = (message: ChatServerMessage) => (message.inserted_at ? Date.parse(message.inserted_at) : 0)
